import PauseIcon from "@mui/icons-material/Pause";
import { Box, Chip, Skeleton, Stack, TableCell, Tooltip, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_ACTIVITIES_TEXT_KEYS,
  slaActivityWorkingDuration,
} from "model/slaActivitiesModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import { SLA_HEALTH_ON_TIME } from "model/slaModel";
import React, { useEffect, useRef } from "react";
import { healthChipColor } from "./SlaTaskSlaBadge";
import { useSlaActivities, useSlaActivityForTask } from "./slaActivitiesContext";
import { slaProcessIsWorse, useSlaInstanceSummary } from "./SlaInstanceSummaryLine";

/**
 * **Colonne SLA della lista viva** (Operazioni > In tempo reale): stato,
 * tempo restante e scadenza di ogni riga, solo con lo SLA dell'organizzazione
 * attivo.
 *
 * Regole:
 * - i dati vengono dal `SlaActivitiesProvider` che avvolge la tabella (una sola
 *   lettura di `GET /sla/organizations/{org}/instances`, nessuna chiamata per
 *   riga); l'unione con le righe e' nel browser;
 * - per riga vale l'istanza **di attivita'** sul nodo del token
 *   (`designerid`), altrimenti quella **di processo** (`slaActivityForTask`);
 *   senza istanza le tre celle mostrano "—"; con lo SLA di attivita' vivo, lo
 *   SLA del processo resta leggibile nel tooltip di salute e residuo;
 * - la salute e' quella viva (`slaActivityLiveHealth` via `slaActivityDetail`):
 *   cambia alla scadenza senza attendere il refetch;
 * - il contesto cambia ogni secondo (countdown): solo queste celle si
 *   ridisegnano, non la riga intera (`TaskItem` resta memoizzato);
 * - la scadenza e' la vigente nella timezone dell'istanza, come nel Monitor;
 * - la colonna della scadenza si nasconde sotto `md` (tabella gia' larga).
 */

/** Scadenza: nascosta sugli schermi stretti. */
const DEADLINE_CELL_SX = { display: { xs: "none", md: "table-cell" } } as const;
const CELL_SX = { whiteSpace: "nowrap" } as const;

interface SlaLiveRowCellsProps {
  idprocess: number;
  idprocesstoken: number;
  designerid?: string | null;
}

/**
 * Intestazione della colonna di salute: chiave propria (`sla.live.column.health`),
 * distinta dalla colonna "Stato" del processo; il testo viene solo dal catalogo.
 */
export const SLA_LIVE_COLUMN_HEALTH_KEY = "sla.live.column.health";

export const SlaLiveHeaderCells: React.FC = () => {
  const translations = useTranslations();
  return (
    <>
      <TableCell sx={{ width: "8%", ...CELL_SX }}>
        {getTranslation(translations, SLA_LIVE_COLUMN_HEALTH_KEY)}
      </TableCell>
      <TableCell sx={{ width: "8%", ...CELL_SX }}>
        {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.columnRemaining)}
      </TableCell>
      <TableCell sx={{ width: "10%", ...CELL_SX, ...DEADLINE_CELL_SX }}>
        {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.columnDeadline)}
      </TableCell>
    </>
  );
};

export const SlaLiveSkeletonCells: React.FC = () => (
  <>
    <TableCell>
      <Skeleton variant="rounded" width={64} height={20} sx={{ borderRadius: 1.5 }} />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width={56} height={16} />
    </TableCell>
    <TableCell sx={DEADLINE_CELL_SX}>
      <Skeleton variant="text" width={96} height={16} />
    </TableCell>
  </>
);

const EMPTY = "—";

export const SlaLiveRowCells: React.FC<SlaLiveRowCellsProps> = ({
  idprocess,
  idprocesstoken,
  designerid,
}) => {
  const translations = useTranslations();
  const context = useSlaActivities();
  const view = useSlaActivityForTask({ idprocess, designerid: designerid || null });
  const prefix = `monitor-sla-${idprocesstoken}`;
  const activitySummary = useSlaInstanceSummary(view, "activity");
  const processSummary = useSlaInstanceSummary(view?.process ?? null, "process");

  if (!view) {
    return (
      <>
        <TableCell data-testid={`${prefix}-health`}>{EMPTY}</TableCell>
        <TableCell data-testid={`${prefix}-remaining`}>{EMPTY}</TableCell>
        <TableCell data-testid={`${prefix}-deadline`} sx={DEADLINE_CELL_SX}>
          {EMPTY}
        </TableCell>
      </>
    );
  }

  const { instance, detail, countdown } = view;
  const locale = context?.locale || "en";
  const overdueSeconds = countdown.overdueSeconds ?? 0;
  const isOverdue = overdueSeconds > 0;
  const duration = slaActivityWorkingDuration(
    isOverdue ? overdueSeconds : countdown.remainingSeconds,
    locale,
  );
  const durationText = duration
    ? `${duration.value} ${getTranslation(translations, duration.unitKey)}`
    : null;
  const healthLabel = detail.healthKey
    ? getTranslation(translations, detail.healthKey)
    : EMPTY;
  const { process } = view;
  const processWorse = slaProcessIsWorse(view, process);
  /** Tooltip con i due SLA: solo quando il processo ha uno SLA proprio. */
  const processTooltip = process ? (
    <Stack spacing={0.25}>
      <span>{activitySummary}</span>
      <Box component="span" sx={{ fontWeight: processWorse ? 700 : 400 }}>
        {processSummary}
      </Box>
    </Stack>
  ) : (
    ""
  );

  return (
    <>
      <TableCell
        data-testid={`${prefix}-health`}
        data-instance-id={instance.id}
        data-health={detail.health === null ? "" : detail.health}
        data-process-health={process?.detail.health ?? ""}
        data-process-worse={processWorse}
        sx={CELL_SX}
      >
        {detail.health === null ? (
          EMPTY
        ) : (
          <Tooltip title={processTooltip} arrow placement="top">
            <Chip
              size="small"
              color={healthChipColor(detail.health)}
              variant={detail.health === SLA_HEALTH_ON_TIME ? "outlined" : "filled"}
              label={healthLabel}
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "6px",
                "& .MuiChip-label": { px: 0.75 },
                ...(processWorse && {
                  outline: "1.5px dashed",
                  outlineColor: `${healthChipColor(process?.detail.health ?? null)}.main`,
                  outlineOffset: "1px",
                }),
              }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell
        data-testid={`${prefix}-remaining`}
        data-working-seconds={countdown.remainingSeconds ?? ""}
        data-overdue-seconds={countdown.overdueSeconds ?? ""}
        data-clock-running={String(countdown.running)}
        aria-live="off"
        sx={CELL_SX}
      >
        <Tooltip title={processTooltip} arrow placement="top">
          <Stack direction="row" spacing={0.5} alignItems="center">
            {countdown.paused && (
              <PauseIcon
                sx={{ fontSize: 14 }}
                color="action"
                aria-label={getTranslation(
                  translations,
                  SLA_ACTIVITIES_TEXT_KEYS.countdownPaused,
                )}
              />
            )}
            <Typography
              variant="body2"
              color={isOverdue ? "error" : undefined}
              fontWeight={isOverdue ? 600 : undefined}
            >
              {durationText === null
                ? EMPTY
                : isOverdue
                  ? `${getTranslation(
                      translations,
                      SLA_MONITOR_TEXT_KEYS.columnOverdue,
                    )} +${durationText}`
                  : durationText}
            </Typography>
          </Stack>
        </Tooltip>
      </TableCell>
      <TableCell
        data-testid={`${prefix}-deadline`}
        data-timezone={detail.timezoneId}
        sx={{ ...CELL_SX, ...DEADLINE_CELL_SX }}
      >
        <Typography variant="body2">{detail.effectiveLabel || EMPTY}</Typography>
        {detail.overridden && (
          <Typography variant="caption" component="div" color="text.secondary">
            {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.overridden)}
          </Typography>
        )}
      </TableCell>
    </>
  );
};

/**
 * Rilegge le istanze quando la lista viva si rilegge (`refreshKey`). Il primo
 * valore e' coperto dalla lettura al mount del provider.
 */
export const SlaLiveRefreshOnChange: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const context = useSlaActivities();
  const refreshRef = useRef(context?.refresh);
  refreshRef.current = context?.refresh;
  const firstRef = useRef(true);
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    refreshRef.current?.();
  }, [refreshKey]);
  return null;
};
